import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Printer, Edit, Trash2, BarChart2, Filter, Calendar, FileText, ArrowRight } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { Report, ReportStatus, ReportType } from '../../types';
import useAppStore from '../../store/useAppStore';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const statusVariant: { [key in ReportStatus]: string } = {
    [ReportStatus.Approved]: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    [ReportStatus.Pending]: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    [ReportStatus.Rejected]: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const typeColors: { [key in ReportType]: string } = {
    [ReportType.Sales]: 'text-blue-400',
    [ReportType.Maintenance]: 'text-emerald-400',
    [ReportType.Inquiry]: 'text-slate-400',
    [ReportType.Project]: 'text-amber-400',
};

const ModernReportsLogScreen: React.FC = () => {
    const { t, user } = useAppContext();
    const {
        reports,
        printReport,
        deleteReport,
        openConfirmation,
        reportsLogFilters,
        setReportsLogFilters,
    } = useAppStore();
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('');

    useEffect(() => {
        if (reportsLogFilters) {
            setStatusFilter(reportsLogFilters.status);
            setReportsLogFilters(null);
        }
    }, [reportsLogFilters, setReportsLogFilters]);

    if (!user) return null;

    const employeeReports = reports.filter(r => r.employeeId === user.employeeId);

    const filteredReports = useMemo(() => {
        return employeeReports.filter(report => {
            const searchLower = searchTerm.toLowerCase();
            const searchMatch = searchLower === '' ||
                (report.employeeName || '').toLowerCase().includes(searchLower) ||
                (report.id || '').toLowerCase().includes(searchLower) ||
                (report.type || '').toLowerCase().includes(searchLower);

            const typeMatch = typeFilter === 'all' || report.type === typeFilter;
            const statusMatch = statusFilter === 'all' || report.status === statusFilter;

            const dateMatch = dateFilter === '' ||
                format(new Date(report.date), 'yyyy-MM-dd') === dateFilter;

            return searchMatch && typeMatch && statusMatch && dateMatch;
        });
    }, [employeeReports, searchTerm, typeFilter, statusFilter, dateFilter]);

    const handleDelete = (reportId: string) => {
        if (confirm('هل أنت متأكد من حذف هذا التقرير؟ لا يمكن التراجع عن هذا الإجراء.')) {
            if (!user) return;
            deleteReport(reportId, user);
            toast.success('تم حذف التقرير بنجاح');
        }
    };

    const handleEdit = (report: Report) => {
        const editPathMap: { [key in ReportType]?: string } = {
            [ReportType.Sales]: `/sales/edit/${report.id}`,
            [ReportType.Maintenance]: `/maintenance/edit/${report.id}`,
            [ReportType.Project]: `/projects/edit/${report.id}`,
        };
        const path = editPathMap[report.type];
        if (path) {
            navigate(path);
        } else {
            toast.error('لا يمكن تعديل هذا النوع من التقارير.');
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">سجل التقارير</h1>
                    <p className="text-slate-400">استعراض وإدارة جميع التقارير الخاصة بك</p>
                </div>
                <button
                    onClick={() => navigate('/')}
                    className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                >
                    <ArrowRight size={18} />
                    <span>رجوع</span>
                </button>
            </div>

            {/* Filters */}
            <div className="glass-card p-6 rounded-2xl space-y-4">
                <div className="flex items-center gap-2 text-white font-medium mb-2">
                    <Filter size={20} className="text-blue-400" />
                    <span>تصفية التقارير</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="relative">
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="بحث..."
                            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 pr-10 pl-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                    </div>
                    <div className="relative">
                        <FileText className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                        <select
                            value={typeFilter}
                            onChange={e => setTypeFilter(e.target.value)}
                            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 pr-10 pl-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                        >
                            <option value="all">كل الأنواع</option>
                            {Object.values(ReportType).map(type => <option key={type} value={type}>{type}</option>)}
                        </select>
                    </div>
                    <div className="relative">
                        <Filter className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                        <select
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 pr-10 pl-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                        >
                            <option value="all">كل الحالات</option>
                            {Object.values(ReportStatus).map(status => <option key={status} value={status}>{status}</option>)}
                        </select>
                    </div>
                    <div className="relative">
                        <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                        <input
                            type="date"
                            value={dateFilter}
                            onChange={e => setDateFilter(e.target.value)}
                            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 pr-10 pl-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                    </div>
                </div>
            </div>

            {/* Reports List */}
            {filteredReports.length > 0 ? (
                <div className="glass-card rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left rtl:text-right text-slate-400">
                            <thead className="text-xs text-slate-300 uppercase bg-slate-800/50 border-b border-white/5">
                                <tr>
                                    <th scope="col" className="px-6 py-4">الموظف</th>
                                    <th scope="col" className="px-6 py-4">الفرع</th>
                                    <th scope="col" className="px-6 py-4">النوع</th>
                                    <th scope="col" className="px-6 py-4">التاريخ</th>
                                    <th scope="col" className="px-6 py-4">الحالة</th>
                                    <th scope="col" className="px-6 py-4">إجراءات</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredReports.map((report) => (
                                    <tr key={report.id} className="bg-transparent border-b border-white/5 hover:bg-white/5 transition-colors">
                                        <td className="px-6 py-4 font-medium text-white whitespace-nowrap">{report.employeeName}</td>
                                        <td className="px-6 py-4">{report.branch}</td>
                                        <td className={`px-6 py-4 font-medium ${typeColors[report.type]}`}>{report.type}</td>
                                        <td className="px-6 py-4">{new Date(report.date).toLocaleDateString('ar-SA')}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusVariant[report.status]}`}>
                                                {report.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/reports/${report.id}`)}
                                                    className="p-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
                                                    title="عرض"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button
                                                    onClick={() => printReport(report.id)}
                                                    className="p-2 rounded-lg bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 transition-colors"
                                                    title="طباعة"
                                                >
                                                    <Printer size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(report)}
                                                    className="p-2 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors"
                                                    title="تعديل"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(report.id)}
                                                    className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                                                    title="حذف"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="glass-card p-12 rounded-2xl flex flex-col items-center justify-center text-center">
                    <div className="w-20 h-20 rounded-full bg-slate-800/50 flex items-center justify-center mb-6">
                        <BarChart2 size={40} className="text-slate-600" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">لا توجد تقارير مطابقة</h3>
                    <p className="text-slate-400 max-w-md mx-auto mb-8">
                        {employeeReports.length > 0
                            ? "لا توجد تقارير تطابق معايير الفلترة الحالية. حاول تغيير معايير البحث."
                            : "لم تقم بإنشاء أي تقارير بعد. اذهب إلى لوحة التحكم لإنشاء تقرير جديد."}
                    </p>
                    {employeeReports.length === 0 && (
                        <button
                            onClick={() => navigate('/')}
                            className="px-6 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors"
                        >
                            الذهاب للوحة التحكم
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ModernReportsLogScreen;
